import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Info, RefreshCw, FileText, FileSpreadsheet, Wrench, Mail } from "lucide-react";
import { BarChart, Bar, Line, ComposedChart, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, API, fmtINR, fmtDate, apiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const iso = (d) => d.toISOString().slice(0, 10);
const today = () => iso(new Date());
const presetRange = (p, earliest) => {
  const n = new Date();
  if (p === "month") return [iso(new Date(n.getFullYear(), n.getMonth(), 1)), today()];
  if (p === "quarter") return [iso(new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3, 1)), today()];
  if (p === "year") return [iso(new Date(n.getFullYear(), 0, 1)), today()];
  return [earliest || "", today()];
};
const PRESETS = [["month", "This Month"], ["quarter", "This Quarter"], ["year", "This Year"], ["all", "All Available Data"], ["custom", "Custom Range"]];
const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toFixed(2)}%`);

const Metric = ({ label, value, sub, tone = "", testid }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-lg p-4 shadow-sm">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-xl font-mono font-semibold tabular-nums mt-2 break-all ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const Table = ({ title, cols, rows, testid, empty }) => (
  <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
    <div className="px-4 py-3 border-b border-slate-200"><h2 className="text-base font-semibold text-slate-800 font-head">{title}</h2></div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="bg-slate-50 border-b border-slate-200">
          {cols.map((c) => <th key={c.k} className={`px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 ${c.num ? "text-right" : "text-left"}`}>{c.l}</th>)}
        </tr></thead>
        <tbody>
          {!rows.length ? <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">{empty || "No data"}</td></tr>
            : rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                {cols.map((c) => <td key={c.k} className={`px-4 py-2.5 whitespace-nowrap ${c.num ? "text-right font-mono tabular-nums" : "text-slate-700"} ${c.tone ? c.tone(r) : ""}`}>{c.f ? c.f(r) : (r[c.k] ?? "-")}</td>)}
              </tr>))}
        </tbody>
      </table>
    </div>
  </div>
);

export default function ProfitLoss() {
  const [d, setD] = useState(null);
  const [fr, setFr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("all");
  const [range, setRange] = useState({ start: "", end: today() });
  const [tab, setTab] = useState("monthly");
  const [fixOpen, setFixOpen] = useState(false);
  const [fixRows, setFixRows] = useState([]);
  const [rates, setRates] = useState({});
  const [savingFix, setSavingFix] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const emailPl = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post("/reports/pl/email", { month: (range.end || today()).slice(0, 7) });
      toast.success(data.message);
      if (data.whatsapp_link) window.open(data.whatsapp_link, "_blank");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setEmailing(false);
  };

  const download = (kind) => {
    const token = localStorage.getItem("token");
    const q = `token=${token}&start=${range.start || ""}&end=${range.end || ""}`;
    window.open(kind === "pdf" ? `${API}/pdf/pl?${q}` : `${API}/reports/pl/xlsx?${q}`, "_blank");
  };

  const openFixer = async () => {
    setFixOpen(true);
    setRates({});
    try { const { data } = await api.get("/products/missing-cost"); setFixRows(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const saveRates = async () => {
    const payload = Object.entries(rates).filter(([, v]) => Number(v) > 0).map(([product_id, v]) => ({ product_id, purchase_rate: Number(v) }));
    if (!payload.length) return toast.error("Enter a purchase cost for at least one product");
    setSavingFix(true);
    try {
      const { data } = await api.post("/products/purchase-rates", { rates: payload });
      toast.success(data.message);
      setFixOpen(false);
      load(range);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSavingFix(false);
  };

  const load = async (r) => {
    setLoading(true);
    try {
      const qs = [r.start && `start=${r.start}`, r.end && `end=${r.end}`].filter(Boolean).join("&");
      const { data } = await api.get(`/reports/pl${qs ? `?${qs}` : ""}`);
      setD(data);
      setRange({ start: data.start, end: data.end });
      api.get(`/reports/freight-check?start=${data.start}&end=${data.end}`).then((x) => setFr(x.data)).catch(() => setFr(null));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  useEffect(() => { load({ start: "", end: "" }); }, []);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "custom") return;
    const [s, e] = presetRange(p, d?.earliest_transaction);
    setRange({ start: s, end: e });
    load({ start: s, end: e });
  };

  const s = d?.summary || {};
  const loss = Number(s.net_profit || 0) < 0;
  const chart = (d?.monthly || []).map((m) => ({ name: m.month, Revenue: m.revenue, "Net Profit": m.net_profit }));

  return (
    <div data-testid="profit-loss-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 font-head">Profit &amp; Loss</h1>
          <p className="text-sm text-slate-500 mt-0.5" data-testid="pl-period">
            {d ? `${fmtDate(d.start)} — ${fmtDate(d.end)}` : "Loading..."}
            {d?.earliest_transaction ? ` · earliest record ${fmtDate(d.earliest_transaction)}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          {PRESETS.map(([k, l]) => (
            <Button key={k} data-testid={`pl-preset-${k}`} size="sm" variant={preset === k ? "default" : "outline"}
              className={`rounded-sm h-9 ${preset === k ? "bg-[#0B132B] hover:bg-[#1C2541] text-white" : ""}`} onClick={() => applyPreset(k)}>{l}</Button>
          ))}
          {preset === "custom" && (
            <>
              <Input data-testid="pl-start" type="date" value={range.start || ""} onChange={(e) => setRange((p) => ({ ...p, start: e.target.value }))} className="bg-white h-9 w-40" />
              <Input data-testid="pl-end" type="date" value={range.end || ""} onChange={(e) => setRange((p) => ({ ...p, end: e.target.value }))} className="bg-white h-9 w-40" />
              <Button data-testid="pl-apply" size="sm" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm h-9" onClick={() => load(range)}>Apply</Button>
              <Button data-testid="pl-reset" size="sm" variant="outline" className="rounded-sm h-9" onClick={() => applyPreset("all")}>Reset</Button>
            </>
          )}
          <Button data-testid="pl-pdf-btn" size="sm" variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => download("pdf")}><FileText className="w-4 h-4" /> PDF</Button>
          <Button data-testid="pl-xlsx-btn" size="sm" variant="outline" className="rounded-sm h-9 gap-1.5" onClick={() => download("xlsx")}><FileSpreadsheet className="w-4 h-4" /> Excel</Button>
          <Button data-testid="pl-email-btn" size="sm" variant="outline" className="rounded-sm h-9 gap-1.5" onClick={emailPl} disabled={emailing}><Mail className="w-4 h-4" /> {emailing ? "Sending..." : "Email P&L"}</Button>
          <Button data-testid="pl-fix-costs-header-btn" size="sm" variant="outline" className="rounded-sm h-9 gap-1.5" onClick={openFixer}><Wrench className="w-4 h-4" /> Fix Costs</Button>
          <Button data-testid="pl-refresh" size="icon" variant="outline" className="rounded-sm h-9 w-9" onClick={() => load(range)}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      {loading && !d ? <div className="ab-skeleton h-40" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-5" data-testid="pl-summary">
            <Metric testid="pl-revenue" label="Net Revenue" value={fmtINR(s.revenue)} sub={`${s.invoice_count || 0} invoices${s.sales_returns ? ` · less ${fmtINR(s.sales_returns)} returns` : ""} · excl. GST`} />
            <Metric testid="pl-cogs" label="COGS" value={fmtINR(s.cogs)} sub={s.cogs_coverage_pct === null ? "no sales" : `${s.cogs_coverage_pct}% qty costed`} />
            <Metric testid="pl-gross-profit" label="Gross Profit" value={fmtINR(s.gross_profit)} sub={`Margin ${pct(s.gross_margin_pct)}`} tone="text-emerald-700" />
            <Metric testid="pl-expenses" label="Operating Expenses" value={fmtINR(s.expenses)} sub="vouchers + payroll + wages" tone="text-rose-700" />
            <Metric testid="pl-net-profit" label={loss ? "Net Loss" : "Net Profit"} value={fmtINR(s.net_profit)} tone={loss ? "text-red-700" : "text-emerald-700"} sub={`Gross profit − expenses`} />
            <Metric testid="pl-margin" label="Profit Margin" value={pct(s.margin_pct)} sub={s.revenue > 0 ? "of net revenue" : "no revenue in period"} tone={loss ? "text-red-700" : "text-emerald-700"} />
          </div>

          {(d?.cost_unavailable_products?.length > 0) && (
            <div data-testid="pl-cost-warning" className="mb-5 flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <div>No purchase cost recorded for: <b>{d.cost_unavailable_products.join(", ")}</b>. Their quantity is excluded from COGS — nothing is estimated. Add a purchase rate or purchase bill for accurate profit.</div>
              <Button data-testid="pl-fix-costs-btn" size="sm" className="ml-auto shrink-0 bg-amber-600 hover:bg-amber-700 text-white rounded-sm gap-1.5" onClick={openFixer}><Wrench className="w-4 h-4" /> Fix Costs</Button>
            </div>
          )}

          <div className="flex gap-2 mb-4 flex-wrap">
            {[["monthly", "Monthly P&L"], ["product", "By Product"], ["customer", "By Customer"], ["freight", "Freight Check"], ["detail", "Detailed P&L"]].map(([k, l]) => (
              <Button key={k} data-testid={`pl-tab-${k}`} size="sm" variant={tab === k ? "default" : "outline"}
                className={`rounded-sm ${tab === k ? "bg-[#0B132B] hover:bg-[#1C2541] text-white" : ""}`} onClick={() => setTab(k)}>{l}</Button>
            ))}
          </div>

          {tab === "monthly" && (
            <div className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
                <h2 className="text-base font-semibold text-slate-800 font-head mb-4">Revenue vs Net Profit</h2>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={{ stroke: "#e2e8f0" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ background: "#0B132B", border: "none", borderRadius: 4, color: "#fff" }} formatter={(v) => fmtINR(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="Revenue" fill="#1C2541" radius={[3, 3, 0, 0]} maxBarSize={48} />
                    <Line type="monotone" dataKey="Net Profit" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <Table title="Monthly Profit & Loss" testid="pl-monthly-table" empty="No financial records yet"
                rows={d?.monthly || []}
                cols={[{ k: "month", l: "Month" }, { k: "revenue", l: "Revenue", num: true, f: (r) => fmtINR(r.revenue) },
                  { k: "cogs", l: "COGS", num: true, f: (r) => fmtINR(r.cogs) },
                  { k: "gross_profit", l: "Gross Profit", num: true, f: (r) => fmtINR(r.gross_profit) },
                  { k: "expenses", l: "Op. Expenses", num: true, f: (r) => fmtINR(r.expenses) },
                  { k: "net_profit", l: "Net Profit/Loss", num: true, f: (r) => fmtINR(r.net_profit), tone: (r) => (r.net_profit < 0 ? "text-red-700" : "text-emerald-700") },
                  { k: "margin_pct", l: "Margin %", num: true, f: (r) => pct(r.margin_pct) }]} />
            </div>
          )}

          {tab === "product" && (
            <Table title="Profit by Product" testid="pl-product-table" empty="No sales in this period" rows={d?.by_product || []}
              cols={[{ k: "product", l: "Product" }, { k: "qty", l: "Qty Sold", num: true, f: (r) => Number(r.qty || 0).toLocaleString("en-IN") },
                { k: "revenue", l: "Revenue", num: true, f: (r) => fmtINR(r.revenue) },
                { k: "unit_cost", l: "Cost/Unit", num: true, f: (r) => (r.unit_cost ? fmtINR(r.unit_cost) : "not recorded") },
                { k: "cost", l: "Product Cost", num: true, f: (r) => fmtINR(r.cost) },
                { k: "gross_profit", l: "Gross Profit", num: true, f: (r) => fmtINR(r.gross_profit), tone: (r) => (r.gross_profit < 0 ? "text-red-700" : "text-emerald-700") },
                { k: "margin_pct", l: "Margin %", num: true, f: (r) => pct(r.margin_pct) }]} />
          )}

          {tab === "customer" && (
            <Table title="Profit by Customer" testid="pl-customer-table" empty="No sales in this period" rows={d?.by_customer || []}
              cols={[{ k: "customer", l: "Customer" }, { k: "invoices", l: "Invoices", num: true },
                { k: "revenue", l: "Revenue", num: true, f: (r) => fmtINR(r.revenue) },
                { k: "cost", l: "Cost", num: true, f: (r) => fmtINR(r.cost) },
                { k: "gross_profit", l: "Gross Profit", num: true, f: (r) => fmtINR(r.gross_profit), tone: (r) => (r.gross_profit < 0 ? "text-red-700" : "text-emerald-700") },
                { k: "margin_pct", l: "Margin %", num: true, f: (r) => pct(r.margin_pct) }]} />
          )}

          {tab === "freight" && (
            <div className="space-y-4" data-testid="pl-freight">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Metric testid="freight-billed" label="Transport Billed" value={fmtINR(fr?.totals?.transport_billed)} sub={`${fr?.totals?.invoice_count || 0} invoices`} />
                <Metric testid="freight-cost" label="Actual Freight Cost" value={fmtINR(fr?.totals?.freight_cost)} sub="linked expense vouchers" tone="text-rose-700" />
                <Metric testid="freight-difference" label={Number(fr?.totals?.difference || 0) < 0 ? "Short Recovery" : "Recovered Extra"} value={fmtINR(fr?.totals?.difference)}
                  tone={Number(fr?.totals?.difference || 0) < 0 ? "text-red-700" : "text-emerald-700"} sub={`${fr?.totals?.short_count || 0} invoice(s) short`} />
                <Metric testid="freight-unlinked" label="Unlinked Freight" value={fmtINR(fr?.totals?.unlinked_total)} sub="not tied to any invoice" />
              </div>
              <Table title="Transport billed vs actual cost per invoice" testid="freight-table" empty="No transport billed or freight expenses in this period" rows={fr?.rows || []}
                cols={[{ k: "invoice_date", l: "Date", f: (r) => fmtDate(r.invoice_date) }, { k: "invoice_no", l: "Invoice" }, { k: "customer_name", l: "Customer" },
                  { k: "transport_billed", l: "Billed", num: true, f: (r) => fmtINR(r.transport_billed) },
                  { k: "freight_cost", l: "Actual Cost", num: true, f: (r) => fmtINR(r.freight_cost) },
                  { k: "difference", l: "Difference", num: true, f: (r) => fmtINR(r.difference), tone: (r) => (r.difference < 0 ? "text-red-700" : "text-emerald-700") },
                  { k: "state", l: "Result", tone: (r) => (r.state === "Short recovery" ? "text-red-700 font-medium" : r.state === "Covered" ? "text-emerald-700 font-medium" : "text-amber-700") },
                  { k: "transporters", l: "Transporter" }, { k: "vouchers", l: "Vouchers" }]} />
              <Table title="Freight vouchers not linked to an invoice" testid="freight-unlinked-table" empty="Every freight voucher is linked to an invoice" rows={fr?.unlinked_freight || []}
                cols={[{ k: "date", l: "Date", f: (r) => fmtDate(r.date) }, { k: "voucher_no", l: "Voucher" }, { k: "transporter_name", l: "Transporter" },
                  { k: "vehicle_no", l: "Vehicle" }, { k: "description", l: "Description" },
                  { k: "amount", l: "Amount", num: true, f: (r) => fmtINR(r.amount) }]} />
              <p className="text-xs text-slate-500">{fr?.note}</p>
            </div>
          )}

          {tab === "detail" && (
            <div className="space-y-4">
              <Table title="Expense Breakup (Operating Expenses)" testid="pl-expense-breakup" empty="No expenses in this period" rows={d?.expense_breakup || []}
                cols={[{ k: "category", l: "Category" }, { k: "amount", l: "Amount", num: true, f: (r) => fmtINR(r.amount) }]} />
              <Table title="Revenue — Invoices" testid="pl-detail-revenue" empty="No invoices in this period" rows={d?.detail?.revenue || []}
                cols={[{ k: "date", l: "Date", f: (r) => fmtDate(r.date) }, { k: "ref", l: "Invoice" }, { k: "party", l: "Customer" },
                  { k: "product", l: "Product" }, { k: "quantity", l: "Qty", num: true }, { k: "discount", l: "Discount", num: true, f: (r) => fmtINR(r.discount) },
                  { k: "net_revenue", l: "Net Revenue", num: true, f: (r) => fmtINR(r.net_revenue) },
                  { k: "invoice_total", l: "Invoice Total", num: true, f: (r) => fmtINR(r.invoice_total) }, { k: "status", l: "Status" }]} />
              <Table title="Sales Returns (reduce revenue &amp; reverse cost)" testid="pl-detail-returns" empty="No sales returns in this period" rows={d?.detail?.returns || []}
                cols={[{ k: "date", l: "Date", f: (r) => fmtDate(r.date) }, { k: "ref", l: "Credit Note" }, { k: "invoice", l: "Against Invoice" },
                  { k: "party", l: "Customer" }, { k: "product", l: "Product" }, { k: "quantity", l: "Qty", num: true },
                  { k: "net_revenue", l: "Revenue Reversed", num: true, f: (r) => fmtINR(r.net_revenue) },
                  { k: "cost_reversed", l: "Cost Reversed", num: true, f: (r) => fmtINR(r.cost_reversed) }, { k: "reason", l: "Reason" }]} />
              <Table title="COGS — Cost of Units Sold" testid="pl-detail-cogs" empty="No costed sales in this period" rows={d?.detail?.cogs || []}
                cols={[{ k: "date", l: "Date", f: (r) => fmtDate(r.date) }, { k: "ref", l: "Invoice" }, { k: "product", l: "Product" },
                  { k: "quantity", l: "Qty", num: true }, { k: "unit_cost", l: "Cost/Unit", num: true, f: (r) => (r.unit_cost ? fmtINR(r.unit_cost) : "not recorded") },
                  { k: "cost", l: "Cost", num: true, f: (r) => fmtINR(r.cost) },
                  { k: "cost_source", l: "Cost Source", f: (r) => ({ purchase_avg: "Purchase bills (avg)", product_master: "Product master rate", unavailable: "Not recorded" }[r.cost_source]) }]} />
              <Table title="Operating Expenses — Vouchers" testid="pl-detail-expenses" empty="No expense vouchers in this period" rows={d?.detail?.expenses || []}
                cols={[{ k: "date", l: "Date", f: (r) => fmtDate(r.date) }, { k: "ref", l: "Voucher" }, { k: "category", l: "Category" },
                  { k: "description", l: "Description" }, { k: "amount", l: "Amount", num: true, f: (r) => fmtINR(r.amount) }, { k: "status", l: "Status" }]} />
            </div>
          )}

          <div className="mt-5 bg-slate-50 border border-slate-200 rounded-lg p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">How these numbers are calculated</div>
            <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
              {(d?.notes || []).map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        </>
      )}
      <Dialog open={fixOpen} onOpenChange={setFixOpen}>
        <DialogContent className="max-w-2xl" data-testid="missing-cost-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Products without a purchase cost</DialogTitle>
            <DialogDescription>
              These products have no purchase bill and no purchase rate, so their sales cannot be costed and are left out of COGS.
              Enter the real cost per unit and save — the P&amp;L will recalculate immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto border border-slate-200 rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b border-slate-200 sticky top-0">
                {["Product", "Unit", "Qty Sold", "Sales Rate", "Purchase Cost / Unit"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500">{h}</th>))}
              </tr></thead>
              <tbody>
                {!fixRows.length ? <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400" data-testid="missing-cost-empty">Every product has a recorded purchase cost</td></tr>
                  : fixRows.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100" data-testid={`missing-cost-row-${p.id}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{p.product_name}<div className="text-[10px] text-slate-400">{p.grade || "-"}</div></td>
                      <td className="px-3 py-2 text-slate-500">{p.unit || "-"}</td>
                      <td className="px-3 py-2 font-mono tabular-nums">{Number(p.qty_sold || 0).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 font-mono tabular-nums text-slate-500">{p.sales_rate ? fmtINR(p.sales_rate) : "-"}</td>
                      <td className="px-3 py-2">
                        <Input data-testid={`missing-cost-input-${p.id}`} type="number" min="0" step="0.01" placeholder="0.00" className="h-8 w-32"
                          value={rates[p.id] ?? ""} onChange={(e) => setRates((r) => ({ ...r, [p.id]: e.target.value }))} />
                      </td>
                    </tr>))}
              </tbody>
            </table>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setFixOpen(false)}>Close</Button>
            <Button data-testid="missing-cost-save" disabled={savingFix || !fixRows.length} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={saveRates}>
              {savingFix ? "Saving..." : "Save Costs"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
